import { useEffect, useRef } from 'react';

/** Seuls les liens vers un site, une adresse e-mail ou un téléphone s'ouvrent. */
const LIEN_PERMIS = /^(https?:|mailto:|tel:)/i;

/**
 * Les liens du PDF (site, Instagram, Taplink…) posés au-dessus de la page
 * dessinée : une page dessinée n'est qu'une image, sans eux rien ne s'ouvre.
 *
 * @param {object} page la page de PDF.js
 * @param {object} vue le cadre de la page dessinée
 * @returns {Promise<HTMLAnchorElement[]>} les liens, placés en pourcentage de la page
 */
export async function liensDeLaPage(page, vue) {
  const annotations = await page.getAnnotations();
  return annotations
    .filter((a) => a.subtype === 'Link' && LIEN_PERMIS.test(a.url || ''))
    .map((a) => {
      const [x1, y1] = vue.convertToViewportPoint(a.rect[0], a.rect[1]);
      const [x2, y2] = vue.convertToViewportPoint(a.rect[2], a.rect[3]);
      const lien = document.createElement('a');
      lien.href = a.url;
      lien.target = '_blank';
      lien.relList.add('noopener', 'noreferrer');
      lien.setAttribute('aria-label', a.url);
      lien.style.cssText =
        'position:absolute;display:block;' +
        `left:${(Math.min(x1, x2) / vue.width) * 100}%;` +
        `top:${(Math.min(y1, y2) / vue.height) * 100}%;` +
        `width:${(Math.abs(x2 - x1) / vue.width) * 100}%;` +
        `height:${(Math.abs(y2 - y1) / vue.height) * 100}%`;
      return lien;
    });
}

/**
 * Les pages d'un PDF, dessinées l'une sous l'autre à la largeur de l'écran.
 *
 * <p>Un PDF ne peut pas s'afficher dans un cadre : la politique de sécurité du
 * site l'interdit, et Chrome sur Android n'en montre de toute façon rien. Les
 * pages sont donc dessinées par PDF.js, chargé seulement à l'ouverture d'un
 * PDF, avec le même rendu sur iPhone, Android et ordinateur. Les liens du
 * document restent cliquables.
 *
 * @param {object} props propriétés
 * @param {Blob} props.blob le fichier PDF
 * @param {() => void} [props.onErreur] appelé si le PDF ne peut pas être lu
 * @returns {import('react').ReactElement} les pages
 */
export function PagesPdf({ blob, onErreur }) {
  const ref = useRef(null);
  const erreur = useRef(onErreur);
  erreur.current = onErreur;

  useEffect(() => {
    let annule = false;
    let tache = null;
    (async () => {
      const [pdfjs, { default: Travailleur }] = await Promise.all([
        import('pdfjs-dist/legacy/build/pdf.mjs'),
        import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'),
      ]);
      if (!pdfjs.GlobalWorkerOptions.workerPort) {
        pdfjs.GlobalWorkerOptions.workerPort = new Travailleur();
      }
      const donnees = new Uint8Array(await blob.arrayBuffer());
      // Pas d'évaluation de code venu du fichier : un PDF reçu reste une image.
      tache = pdfjs.getDocument({ data: donnees, isEvalSupported: false });
      if (annule) {
        tache.destroy().catch(() => {});
        return;
      }
      const doc = await tache.promise;
      const largeur = Math.max(200, (ref.current?.clientWidth || 360) - 16);
      // Assez de points pour agrandir la page jusqu'à quatre fois sans flou.
      const densite = Math.min((window.devicePixelRatio || 1) * 2, 4);
      for (let n = 1; n <= doc.numPages && !annule; n += 1) {
        const page = await doc.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const vue = page.getViewport({ scale: (largeur / base.width) * densite });
        const toile = document.createElement('canvas');
        toile.width = Math.floor(vue.width);
        toile.height = Math.floor(vue.height);
        toile.style.cssText = 'display:block;width:100%;height:auto';
        toile.setAttribute('aria-label', 'Page ' + n);
        await page.render({ canvasContext: toile.getContext('2d'), viewport: vue }).promise;
        const feuille = document.createElement('div');
        feuille.style.cssText =
          'position:relative;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,.35)';
        feuille.appendChild(toile);
        const liens = await liensDeLaPage(page, vue).catch(() => []);
        liens.forEach((l) => feuille.appendChild(l));
        if (annule || !ref.current) break;
        ref.current.appendChild(feuille);
      }
    })().catch(() => {
      if (!annule) erreur.current?.();
    });
    return () => {
      annule = true;
      // Le chargement se libère par sa tâche : le document de PDF.js 6 n'a
      // plus de destroy(), et l'appeler faisait tomber toute l'application
      // à la fermeture de la visionneuse.
      if (tache) tache.destroy().catch(() => {});
    };
  }, [blob]);

  return (
    <div
      ref={ref}
      data-testid="pages-pdf"
      style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 8 }}
    />
  );
}
